import { useState } from 'react';
import { dbService } from '../services/DatabaseService';
import { createDefaultCustomPages, clampDefaultQuestionsPageOrder } from '../utils/questionnaireEditorUtils';
import { describeGeminiApiError } from '../utils/aiPrompts';
import { geminiFetch } from '../services/geminiConfig';

/**
 * AI question/title generation via Gemini API.
 */
export function useEditorAI({ setError, questions, setTitle, aiJobTitle, aiShouldFillDescriptions, applyEditorStructure, setDefaultQuestionsPageOrder }) {
  const [isLoading, setIsLoading] = useState(false);

  const callGeminiAPI = async (prompt, schema) => {
    setIsLoading(true); setError(null);
    const payload = { contents: [{ role: "user", parts: [{ text: prompt }] }], generationConfig: schema ? { responseMimeType: "application/json", responseSchema: schema } : {} };
    try {
      const response = await geminiFetch({ payload });
      if (!response.ok) throw new Error(`API-Fehler ${describeGeminiApiError(response.status, response.statusText)}`);
      const result = await response.json();
      if (result.candidates?.[0]?.content.parts?.[0]) return result.candidates[0].content.parts[0].text;
      throw new Error("Unerwartete API-Antwortstruktur.");
    } catch (err) { setError(err.message); console.error(err); return null; } finally { setIsLoading(false); }
  };

  const generateAIQuestions = async () => {
    if (!aiJobTitle) { setError("Bitte geben Sie eine Berufsbezeichnung ein."); return; }

    try {
      const settings = await dbService.getSettings();
      const questionnairePrompt = settings.questionnairePrompt || "Du erstellst hochspezifische Fragebögen für Positionen in Heidelberg, Deutschland (Altstadt). Fokussiere auf die SPEZIELLE BRANCHE/DOMÄNE, nicht nur allgemeine Fähigkeiten. Erstelle Fragen zu: Branchenspezifisches Fachwissen, Produktkenntnisse, lokale Marktkenntnis, Fachbegriffe und Konzepte, branchenspezifische Erfahrungen. Stelle sicher, dass die Fragen klar, relevant und praxisorientiert sind. Berücksichtige, dass Fachkräfte Branchenterminologie als selbstverständlich betrachten. Erstelle eine ausgewogene Mischung aus 2-3 Textfragen, 1-2 Einzelauswahlfragen, 1-2 Mehrfachauswahlfragen und 1 Rangordnungsfrage. MINDESTENS 1 kreative Persönlichkeits-Textfrage einbauen. Wichtige Informationen in den Fragen selbst integrieren, nicht in Beschreibungen. Geschäftsbegriffe als selbstverständlich voraussetzen. Beschreibungen nur bei Bedarf und kurz halten.";

      const prompt = `Erstelle 4-5 hochspezifische Interviewfragen für einen ${aiJobTitle} in Heidelberg, Deutschland (Altstadt).

${questionnairePrompt}

Gib als JSON-Array zurück mit:
- "type": "text", "single-choice", "multiple-choice" oder "ranking"
- "text": spezifische Hauptfrage (mit HTML, integrierte wichtige Infos)
- "description": kurzer, prägnanter Kontext
- "options": realistische, branchenspezifische Optionen (nur für choice-Fragen und ranking-Fragen)`;

      const schema = { type: "ARRAY", items: { type: "OBJECT", properties: { type: { type: "STRING" }, text: { type: "STRING" }, description: { type: "STRING" }, options: { type: "ARRAY", items: { type: "STRING" } } } } };
      const aiResult = await callGeminiAPI(prompt, schema);
      if (aiResult) {
        try {
          const newAiQuestions = JSON.parse(aiResult);
          const aiDefaultPages = createDefaultCustomPages();
          const specificQuestionsPage = aiDefaultPages[1] || aiDefaultPages[0];
          const formattedQuestions = newAiQuestions.map(q => ({
            ...q,
            id: Date.now() + Math.random(),
            type: (q.type || 'text').trim().toLowerCase(),
            options: q.options || [],
            pageId: specificQuestionsPage?.id,
            pageTitle: specificQuestionsPage?.title || '',
            pageDescription: specificQuestionsPage?.description || '',
            pageOrder: specificQuestionsPage?.order || 1
          }));

          applyEditorStructure(formattedQuestions, aiDefaultPages);
          setDefaultQuestionsPageOrder(clampDefaultQuestionsPageOrder(aiDefaultPages.length + 1, aiDefaultPages.length));
          setTitle('');

          if (!aiShouldFillDescriptions) {
            const questionsWithoutDescriptions = formattedQuestions.map(q => ({
              ...q,
              description: ''
            }));
            applyEditorStructure(questionsWithoutDescriptions, aiDefaultPages);
            setDefaultQuestionsPageOrder(clampDefaultQuestionsPageOrder(aiDefaultPages.length + 1, aiDefaultPages.length));
          }
        } catch (e) { setError("Fehler beim Verarbeiten der KI-Antwort."); console.error(e); }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setError('Fehler beim Laden der Einstellungen');
    }
  };

  const generateAITitle = async () => {
    if (questions.length === 0) { setError("Bitte fügen Sie zuerst Fragen hinzu."); return; }

    try {
      const settings = await dbService.getSettings();
      const questionnairePrompt = settings.questionnairePrompt || "Du erstellst hochspezifische Fragebögen für Positionen in Heidelberg, Deutschland (Altstadt). Fokussiere auf die SPEZIELLE BRANCHE/DOMÄNE, nicht nur allgemeine Fähigkeiten. Erstelle Fragen zu: Branchenspezifisches Fachwissen, Produktkenntnisse, lokale Marktkenntnis, Fachbegriffe und Konzepte, branchenspezifische Erfahrungen. Stelle sicher, dass die Fragen klar, relevant und praxisorientiert sind. Berücksichtige, dass Fachkräfte Branchenterminologie als selbstverständlich betrachten. Erstelle eine ausgewogene Mischung aus 2-3 Textfragen, 1-2 Einzelauswahlfragen, 1-2 Mehrfachauswahlfragen und 1 Rangordnungsfrage. MINDESTENS 1 kreative Persönlichkeits-Textfrage einbauen. Wichtige Informationen in den Fragen selbst integrieren, nicht in Beschreibungen. Geschäftsbegriffe als selbstverständlich voraussetzen. Beschreibungen nur bei Bedarf und kurz halten.";

      const prompt = `Basierend auf diesen Fragen:\n${questions.map(q => q.text).join('\n')}\n\n${questionnairePrompt}\n\nSchlage einen professionellen Titel vor. Gib nur den Titel zurück.`;
      const newTitle = await callGeminiAPI(prompt);
      if (newTitle) setTitle(newTitle.replace(/["']/g, ""));
    } catch (error) {
      console.error('Error loading settings:', error);
      setError('Fehler beim Laden der Einstellungen');
    }
  };

  return { isLoading, generateAIQuestions, generateAITitle };
}
